// Pure metadata table for the admin top-level tabs — no React or screen
// imports here so tests can read it without Jest having to transform every
// screen (and via them, FontAwesome's ESM).
//
// Mirrors the shape of `settings/tabMetadata.ts` but extended with an
// optional `subRoutes` field to handle multi-screen top-level tabs (the
// settings sub-tabs are all single-screen; top-level tabs frequently push
// a detail screen — Memberships → UserMemberships, Benefits → BenefitEdit
// being the existing examples).
//
// Adding a new top-level tab:
//   1. Add an entry below (alphabetical by `key`).
//   2. Wire the screen + any sub-route screens in `topLevelTabRegistry.ts`.
// Done — AdminStack and AdminScreen pick it up automatically.

import type { IconProp } from '@fortawesome/fontawesome-svg-core';

export type AdminTopLevelSection =
  | 'configuration'
  | 'people'
  | 'operations'
  | 'readOnly';

/**
 * One sub-route declaration. The `routeName` is the only required field
 * the screen needs; `title` is used for the navigation header.
 */
export interface AdminTopLevelSubRouteMeta {
  routeName: string;
  title: string;
}

export interface AdminTopLevelTabMeta {
  /** Stable, lowercase slug — used as React key + telemetry id. */
  key: string;
  /** Display name in AdminScreen + the navigation header on push. */
  label: string;
  /** Description shown under the row in AdminScreen. */
  description: string;
  icon: IconProp;
  /** Stack route name for the primary screen of this tab. */
  routeName: string;
  section: AdminTopLevelSection;
  /**
   * Optional additional Stack routes that hang off this tab (e.g.
   * Memberships → UserMemberships, Benefits → BenefitEdit). Mounted
   * by AdminStack but NOT shown in the AdminScreen catalog.
   */
  subRoutes?: AdminTopLevelSubRouteMeta[];
}

export const TOP_LEVEL_SECTION_TITLES: Record<AdminTopLevelSection, string> = {
  configuration: 'Configuration',
  people: 'People',
  operations: 'Operations',
  readOnly: 'Read-only',
};

export const TOP_LEVEL_SECTION_ORDER: AdminTopLevelSection[] = [
  'configuration',
  'people',
  'operations',
  'readOnly',
];

// One entry per tab, alphabetical by `key`. Adding a tab is one new line
// here + one in topLevelTabRegistry.ts (ROUTE_SCREENS map).
//
// `settings` is the special case — its primary screen (AdminSettings) is
// mounted explicitly in AdminStack since it has its OWN nested registry
// of 25 sub-tabs. We surface the row here for AdminScreen to render but
// don't let topLevelTabRegistry override the screen wiring.
export const ALL_TOP_LEVEL_TAB_META: AdminTopLevelTabMeta[] = [
  {
    key: 'abandonedAccounts',
    label: 'Abandoned Accounts',
    description: 'Accounts with no activity, no second member, no paid subscription and no recent sign-in — with the evidence for each, and a manual close that is re-checked, audited and reversible',
    icon: ['fad', 'user-clock'],
    routeName: 'AdminAbandonedAccounts',
    section: 'people',
  },
  {
    key: 'agents',
    label: 'Agents',
    description: 'Monitor patient-agent runs, trigger single-project + nightly cron, inspect each project’s clinical brain',
    icon: ['fad', 'robot'],
    routeName: 'AdminAgents',
    section: 'operations',
    subRoutes: [
      { routeName: 'AdminAgentRunDetail', title: 'Agent Run' },
      { routeName: 'AdminCareAgentOverview', title: 'Care agent' },
    ],
  },
  {
    key: 'aiContext',
    label: 'AI Context',
    description: 'Inspect the base-index snapshot + prompt the model sees at the start of every conversation, per-project',
    icon: ['fad', 'brain-circuit'],
    routeName: 'AdminAIContext',
    section: 'readOnly',
    subRoutes: [
      { routeName: 'AdminAIContextIcd', title: 'ICD-10 Code' },
      { routeName: 'AdminAIContextPrompt', title: 'Prompt Inspector' },
    ],
  },
  {
    key: 'aiCost',
    label: 'AI Cost',
    description: 'What each account / project / user is costing in AI spend — per-source breakdown over 7 / 30 / 90 days',
    icon: ['fad', 'dollar-sign'],
    routeName: 'AdminAICost',
    section: 'readOnly',
  },
  {
    key: 'analytics',
    label: 'Analytics',
    description: 'Prompt analytics — query counts, ratings, model + tool performance, costs, examples',
    icon: ['fad', 'tachometer-alt'],
    routeName: 'AdminAnalytics',
    section: 'readOnly',
    subRoutes: [{ routeName: 'AdminAnalyticsExample', title: 'Query Detail' }],
  },
  {
    key: 'analyticsChat',
    label: 'Analytics Chat',
    description: 'Ask the data anything — product & marketing AI analyst with full searchable history of past questions and answers',
    icon: ['fad', 'comments'],
    routeName: 'AdminAnalyticsChat',
    section: 'readOnly',
  },
  {
    key: 'auditLog',
    label: 'Audit Log',
    description: 'Inspect inserts, updates, and deletes across the platform — filter by table, op, user, or record',
    icon: ['fad', 'clipboard-list'],
    routeName: 'AdminAuditLog',
    section: 'readOnly',
  },
  {
    key: 'benefits',
    label: 'Benefits',
    description: 'Per-state government benefit catalog (CRUD)',
    icon: ['fad', 'hands-helping'],
    routeName: 'AdminBenefits',
    section: 'configuration',
    subRoutes: [{ routeName: 'AdminBenefitEdit', title: 'Benefit' }],
  },
  {
    key: 'cache',
    label: 'Cache',
    description: 'Live cache monitor — hit-rate health, multi-layer status, DynamoDB caches, and clear / warm / reset actions',
    icon: ['fad', 'database'],
    routeName: 'AdminCache',
    section: 'operations',
  },
  {
    key: 'cadence',
    label: 'Cadence',
    description: 'Per-study Cadence defaults — reasoner, prompts, perception, citations, normative bands, and repeatability priors. Drift-tagged at ingest.',
    icon: ['fad', 'waveform-lines'],
    routeName: 'AdminCadence',
    section: 'configuration',
    subRoutes: [
      { routeName: 'AdminCadenceStudyEdit', title: 'Study Defaults' },
      { routeName: 'AdminCadenceCitations', title: 'Citations' },
      { routeName: 'AdminCadenceNormativeBands', title: 'Normative Bands' },
      { routeName: 'AdminCadenceRepeatability', title: 'Repeatability & Pricing' },
    ],
  },
  {
    key: 'cadenceDefaults',
    label: 'Cadence Defaults',
    description: 'Master / system-wide Cadence configuration; templates new studies and feeds the Cadence Mac client.',
    icon: ['fad', 'sliders'],
    routeName: 'AdminCadenceDefaults',
    section: 'configuration',
    subRoutes: [
      { routeName: 'AdminCadenceDefaultsCitations', title: 'Citations' },
      { routeName: 'AdminCadenceDefaultsNormativeBands', title: 'Normative Bands' },
      { routeName: 'AdminCadenceDefaultsRepeatability', title: 'Repeatability' },
      { routeName: 'AdminCadenceDefaultsResearchAudit', title: 'Research Audit' },
    ],
  },
  {
    key: 'campaigns',
    label: 'Email Campaigns',
    description: 'Build triggered, branching email drip workflows; assign templates to steps; target audiences and watch enrollments.',
    icon: ['fad', 'paper-plane'],
    routeName: 'AdminCampaigns',
    section: 'configuration',
    subRoutes: [
      { routeName: 'AdminEditCampaign', title: 'Campaign' },
      { routeName: 'AdminCampaignAnalytics', title: 'Analytics' },
    ],
  },
  {
    key: 'customers',
    label: 'Customers',
    description: 'User list with engagement metrics, search, and re-engagement candidates',
    icon: ['fad', 'users'],
    routeName: 'AdminCustomers',
    section: 'people',
    subRoutes: [{ routeName: 'AdminEditUser', title: 'Edit User' }],
  },
  {
    key: 'diagnoses',
    label: 'Diagnoses',
    description: 'Manage the canonical diagnoses catalog + approve / reject user-submitted diagnosis requests',
    icon: ['fad', 'notes-medical'],
    routeName: 'AdminDiagnoses',
    section: 'configuration',
    subRoutes: [
      { routeName: 'AdminDiagnosisCatalogEdit', title: 'Diagnosis' },
      { routeName: 'AdminDiagnosisRequestResolve', title: 'Resolve Request' },
    ],
  },
  {
    key: 'documents',
    label: 'Documents',
    description: 'Browse the platform document corpus with account / project / static filters',
    icon: ['fad', 'file-alt'],
    routeName: 'AdminDocuments',
    section: 'operations',
  },
  {
    key: 'emailActivity',
    label: 'Email Activity',
    description: 'Per-person view — campaign enrollments, recent sends, and whether the frequency cap is holding their mail',
    icon: ['fad', 'envelope'],
    routeName: 'AdminEmailActivity',
    section: 'readOnly',
  },
  {
    key: 'emailTemplates',
    label: 'Email Templates',
    description: 'Edit transactional email templates, preview, test-send, and cut over to SES',
    icon: ['fad', 'envelope'],
    routeName: 'AdminEmailTemplates',
    section: 'configuration',
    subRoutes: [{ routeName: 'AdminEditEmailTemplate', title: 'Template' }],
  },
  {
    key: 'events',
    label: 'Events',
    description: 'Recent activity across the platform — invites, accounts, subscriptions, users, and projects',
    icon: ['fad', 'calendar-days'],
    routeName: 'AdminEvents',
    section: 'readOnly',
  },
  {
    key: 'helpCenter',
    label: 'Help Center',
    description: 'Manage the public help articles on eleplan.com/help — write, publish, archive (ED-2559)',
    icon: ['fad', 'circle-question'],
    routeName: 'AdminHelpCenter',
    section: 'configuration',
    subRoutes: [{ routeName: 'AdminEditHelpArticle', title: 'Article' }],
  },
  {
    key: 'intakeDrafts',
    label: 'Intake Drafts',
    description: 'Abandoned plan setups and what each collected — every other admin list filters these out',
    icon: ['fad', 'file-circle-question'],
    routeName: 'AdminIntakeDrafts',
    section: 'readOnly',
  },
  {
    key: 'intentApprovals',
    label: 'Intent Approvals',
    description: 'Review and approve / reject proposed changes to the classification intent catalogs — which tools each intent puts in front of the model',
    icon: ['fad', 'route'],
    routeName: 'AdminIntentApprovals',
    section: 'operations',
  },
  {
    key: 'live',
    label: 'Live Users',
    description: "Who's on the app right now, by device (iOS/Android/web/desktop/watch/CarPlay), with plan + session length",
    icon: ['fad', 'users'],
    routeName: 'AdminLive',
    section: 'operations',
    // Tapping a row opens that person's recorded sessions. Capture and replay
    // live on Product Analytics, but that surface is fleet-wide — Live Users
    // already knows who it is looking at, so the drill-down starts there.
    subRoutes: [{ routeName: 'AdminUserSessions', title: 'User Sessions' }],
  },
  {
    key: 'locations',
    label: 'Locations',
    description: 'Live map of every project-member with location sharing enabled',
    icon: ['fad', 'map-location-dot'],
    routeName: 'AdminLocations',
    section: 'operations',
  },
  {
    key: 'marketingAnalytics',
    label: 'Marketing Analytics',
    description: 'Marketing-site traffic, sources, conversions and the visit→register→paid journey — cookieless, with live visitors',
    icon: ['fad', 'bullseye'],
    routeName: 'AdminMarketingAnalytics',
    section: 'readOnly',
  },
  {
    key: 'memberships',
    label: 'Memberships',
    description: 'Manage account + project memberships per user (External flag, removal)',
    icon: ['fad', 'building-user'],
    routeName: 'AdminMemberships',
    section: 'people',
    subRoutes: [
      { routeName: 'AdminUserMemberships', title: 'User Memberships' },
      { routeName: 'AdminAddAccountMembership', title: 'Grant Account Access' },
      { routeName: 'AdminAddProjectMembership', title: 'Add to Project' },
    ],
  },
  {
    key: 'neuralEngine',
    label: 'Neural Engine',
    description: 'Live WebGL visualization of data ingestion + orchestration — documents and reasoning events firing across the pipeline as a neural cortex',
    icon: ['fad', 'brain-circuit'],
    routeName: 'AdminNeuralEngine',
    section: 'operations',
  },
  {
    key: 'noteTemplates',
    label: 'Note Templates',
    description: 'Create, edit, archive system / account note templates',
    icon: ['fad', 'file-pen'],
    routeName: 'AdminNoteTemplates',
    section: 'configuration',
    subRoutes: [{ routeName: 'AdminEditNoteTemplate', title: 'Template' }],
  },
  {
    key: 'opsAssistant',
    label: 'Ops Assistant',
    description: 'Chat with internal operations tools (logs, Lambda, SQS, deployments) — streaming AI assistant',
    icon: ['fad', 'comment-sms'],
    routeName: 'AdminOpsAssistant',
    section: 'operations',
  },
  {
    key: 'optimization',
    label: 'Optimization',
    description: 'Trigger AI rule-optimization proposals per category — review in Rule Approvals',
    icon: ['fad', 'sparkles'],
    routeName: 'AdminOptimization',
    section: 'operations',
  },
  {
    key: 'outreach',
    label: 'Outreach',
    description: 'Emails the Outreach Lead sent from partners@ — delivery, bounces, opt-outs — and who approved or declined each one',
    icon: ['fad', 'paper-plane'],
    routeName: 'AdminOutreach',
    section: 'readOnly',
  },
  {
    key: 'partialSignups',
    label: 'Partial Signups',
    description: 'Captured event leads and abandoned registrations — filter by event, search by name or email, see who became an account',
    icon: ['fad', 'user-plus'],
    routeName: 'AdminPartialSignups',
    section: 'readOnly',
  },
  {
    key: 'partners',
    label: 'Partners',
    description: 'Approve / reject / suspend partner accounts, set tier + limits, browse audit history',
    icon: ['fad', 'handshake'],
    routeName: 'AdminPartners',
    section: 'people',
    subRoutes: [{ routeName: 'AdminPartnerDetail', title: 'Partner' }],
  },
  {
    key: 'personaQa',
    label: 'Persona QA',
    description: 'Persona harness run history — scenario pass/fail + per-step faults',
    icon: ['fad', 'flask-vial'],
    routeName: 'AdminPersonaQA',
    section: 'operations',
  },
  {
    key: 'productAnalytics',
    label: 'Product Analytics',
    description: 'Growth, activation funnel, engagement (DAU/WAU/MAU), drop-off cohorts, plan mix — plus session replay, journeys and bottlenecks from first-party telemetry',
    icon: ['fad', 'chart-line'],
    routeName: 'AdminProductAnalytics',
    section: 'readOnly',
    subRoutes: [{ routeName: 'AdminSessionDetail', title: 'Session Replay' }],
  },
  {
    key: 'prompts',
    label: 'Prompt Costs',
    description: 'AI spend — token + USD totals, audio minutes, document costs, the recent-query cost table, and prompt status counts',
    icon: ['fad', 'file'],
    routeName: 'AdminPrompts',
    section: 'readOnly',
  },
  {
    key: 'pushReceipts',
    label: 'Push / Scheduling',
    description: 'Prompt-delivery pipeline — schedules created vs failed and pushes fired vs failed/expired over today / 7d / 30d, the create-side failures Anticipated never surfaced, plus the 7-day delivery breakdown',
    icon: ['fad', 'bell'],
    routeName: 'AdminPushReceipts',
    section: 'operations',
  },
  {
    key: 'queryProject',
    label: 'Query Project',
    description: 'Run a real Ellie query against any project, 1:1 with the user — reproduces exactly what that project sees (reuses the live KB pipeline, as a project member). Tools run for real and can modify data.',
    icon: ['fad', 'comments'],
    routeName: 'AdminQueryProject',
    section: 'operations',
  },
  {
    key: 'questionEngine',
    label: 'Question Engine',
    description: 'Who answers care-plan questions and how (the care agent from records, or a person by channel), where the engine asked, and the plans with questions waiting',
    icon: ['fad', 'messages-question'],
    routeName: 'AdminQuestionEngine',
    section: 'readOnly',
    subRoutes: [
      { routeName: 'AdminQuestionEngineProjectDetail', title: 'Patient Questions' },
    ],
  },
  {
    key: 'realtime',
    label: 'Realtime Voice',
    description: 'Voice usage, estimated OpenAI costs, organization call outcomes, and session diagnostics',
    icon: ['fad', 'phone-volume'],
    routeName: 'AdminRealtime',
    section: 'operations',
  },
  {
    key: 'ruleApprovals',
    label: 'Rule Approvals',
    description: 'Review and approve / reject AI-led and human-led system rule changes',
    icon: ['fad', 'shield-check'],
    routeName: 'AdminRuleApprovals',
    section: 'operations',
    subRoutes: [{ routeName: 'AdminRuleProposalDetail', title: 'Rule Proposal' }],
  },
  {
    key: 'sessionRecordings',
    label: 'Session Recordings',
    // Says "web only" up front on purpose: the visual replay is rrweb, which
    // only records on web, so an admin who reads this on a phone knows there
    // is no video to go hunting for before they tap in. The event timeline
    // (screens, API calls, errors) is captured on every platform.
    description:
      'Every recorded app session, searchable and paged — window, platform, errors-only and replay-only filters. Tap through for the journey timeline; visual replay is web-only',
    icon: ['fad', 'circle-play'],
    routeName: 'AdminSessionRecordings',
    section: 'readOnly',
    // AdminSessionDetail is deliberately NOT re-declared here — it is already
    // productAnalytics' sub-route, routeNames must be unique across the table,
    // and AdminStack mounts every entry as a flat sibling on one navigator, so
    // this screen can navigate to it from wherever it is registered.
  },
  {
    key: 'settingApprovals',
    label: 'Settings Approvals',
    description: 'Review, approve / reject and roll back proposed changes to settings on system_settings. Every manual settings save in Admin lands here first; nothing changes until it is approved',
    icon: ['fad', 'sliders'],
    routeName: 'AdminSettingApprovals',
    section: 'operations',
  },
  {
    key: 'settings',
    label: 'Settings',
    description: 'Platform, AI, governance, integrations, engagement, and diagnostics',
    icon: ['fad', 'cog'],
    // AdminSettings owns its OWN nested registry (25 sub-tabs); AdminStack
    // mounts it explicitly. topLevelTabRegistry intentionally leaves the
    // screen wiring out — only the catalog row matters.
    routeName: 'AdminSettings',
    section: 'configuration',
  },
  {
    key: 'subscriptions',
    label: 'Subscriptions',
    description: 'Find any active subscription, filter by status / platform, and apply admin overrides (status, balance, period, metadata)',
    icon: ['fad', 'credit-card'],
    routeName: 'AdminSubscriptions',
    section: 'people',
    subRoutes: [
      { routeName: 'AdminSubscriptionDetail', title: 'Subscription' },
    ],
  },
  {
    key: 'support',
    label: 'Support Inbox',
    description: 'Two-way support threads from customers — triage what is open, answer, and close. Was under Settings; it belongs with the rest of the admin surface.',
    icon: ['fad', 'messages'],
    routeName: 'AdminSupportInbox',
    section: 'operations',
    // The inbox's only action is navigate('SupportAdminChat'), so the chat has
    // to be reachable by that exact name from inside AdminStack. Mounting the
    // same screen under one name in several navigators is the pattern this
    // codebase already uses — see the comment in PartnerTabNavigator.
    subRoutes: [{ routeName: 'SupportAdminChat', title: 'Support Thread' }],
  },
  {
    key: 'systemHealth',
    label: 'System Health',
    description: 'Live error feed for every lambda + the mobile/web app (from Better Stack), with AI explanations and email alarm rules',
    icon: ['fad', 'monitor-waveform'],
    routeName: 'AdminSystemHealth',
    section: 'operations',
  },
  {
    key: 'telemetryHealth',
    label: 'Telemetry Health',
    // The one row in this catalog whose subject is the INSTRUMENTATION rather
    // than the product, so the two limits that decide how its numbers should
    // be read belong here, in the searchable description, not two taps in:
    // replay coverage is only meaningful over AUTHENTICATED WEB sessions
    // (rrweb records nowhere else, and anonymous sessions never record by
    // design), and the error counts are unhandled errors only — a failure
    // that was caught and handled never reaches them, so a zero is not a
    // claim that nothing went wrong.
    description:
      'Whether session capture is actually working — volume, signal quality (sessions carrying a real journey vs start-and-vanish), and replay coverage across authenticated web sessions. Visual replay is web-only; error counts cover unhandled errors only',
    icon: ['fad', 'wave-pulse'],
    routeName: 'AdminTelemetryHealth',
    section: 'readOnly',
  },
  {
    key: 'toolDescriptions',
    label: 'Tool Descriptions',
    description: 'Manage tool description overrides + review change proposals',
    icon: ['fad', 'wrench'],
    routeName: 'AdminToolDescriptions',
    section: 'configuration',
    subRoutes: [
      { routeName: 'AdminToolDescriptionEdit', title: 'Tool Description' },
      { routeName: 'AdminToolDescriptionProposal', title: 'Tool Proposal' },
    ],
  },
  {
    key: 'vault',
    label: 'Vault',
    description: 'Server-encrypted demo & test-account credentials (persona-harness logins)',
    icon: ['fad', 'vault'],
    routeName: 'AdminVault',
    section: 'operations',
  },
];

export interface AdminTopLevelSectionGroup<T extends AdminTopLevelTabMeta = AdminTopLevelTabMeta> {
  section: AdminTopLevelSection;
  title: string;
  tabs: T[];
}

/**
 * Returns tabs grouped by section, in TOP_LEVEL_SECTION_ORDER. Sections
 * with no matching tabs are dropped — used by AdminScreen so empty
 * sections never render.
 */
export function getTopLevelSectionsFromMeta<T extends AdminTopLevelTabMeta>(
  tabs: T[],
): AdminTopLevelSectionGroup<T>[] {
  return TOP_LEVEL_SECTION_ORDER.map((section) => ({
    section,
    title: TOP_LEVEL_SECTION_TITLES[section],
    tabs: tabs.filter((t) => t.section === section),
  })).filter((group) => group.tabs.length > 0);
}
